import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, differenceInDays } from 'date-fns';
import { FiDownload as Download, FiPhone as Phone } from 'react-icons/fi';
import { isPolicyCurrent } from '../utils/policyStatus';

const PolicyCard = ({ policy }) => {
  const navigate = useNavigate();
  const getStatusBadge = () => {
    const today = new Date();
    const expDate = new Date(policy.expirationDate);
    const daysUntilExpiry = differenceInDays(expDate, today);

    if (isAfter(today, expDate)) {
      return { label: 'Expired', color: 'bg-red-100 text-red-800' };
    } else if (daysUntilExpiry <= 30) {
      return { label: 'Expiring Soon', color: 'bg-orange-100 text-orange-800' };
    } else {
      return { label: 'Active', color: 'bg-green-100 text-green-800' };
    }
  };

  const status = getStatusBadge();

  const getCarrierLogo = () => {
    // Placeholder for carrier logos
    return (
      <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center text-xs font-medium text-gray-600">
        LOGO
      </div>
    );
  };

  const formatPremium = () => {
    const annual = Number(policy.premium) || 0;
    const isMonthly = policy.paymentFrequency === 'monthly';
    const amount = isMonthly ? annual / 12 : annual;
    return `$${amount.toFixed(2)}${isMonthly ? '/month' : '/year'}`;
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {getCarrierLogo()}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
          {status.label}
        </span>
      </div>

      {/* Policy Info */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{policy.policyType}</h3>
        <p className="text-sm text-gray-600">Policy #: {policy.policyNumber}</p>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Premium:</span>
          <span className="font-medium">{formatPremium()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Expires:</span>
          <span className="font-medium">{format(new Date(policy.expirationDate), 'MMM dd, yyyy')}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {/* "View Details" is gone: there is no policy-detail route to send it
            to. The other two had no onClick either and now go where they say.
            The ID card button is hidden for expired policies, because F12
            deliberately withholds those cards -- a button promising one would
            be a dead end. */}
        {isPolicyCurrent(policy) && (
          <button
            onClick={() => navigate('/dashboard/id-cards')}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download size={16} /> ID Card
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard/claims')}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <Phone size={16} /> File Claim
        </button>
      </div>

      {/* Insured Items */}
      {policy.insuredItems && policy.insuredItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 mb-1">Covered:</div>
          {policy.insuredItems.map((item, index) => (
            <div key={index} className="text-sm text-gray-700">
              {item.type === 'vehicle' 
                ? `${item.year} ${item.make} ${item.model}`
                : item.address}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PolicyCard;